/**
 * O esqueleto: quatro colunas fixas e o conteúdo a fluir no meio.
 *
 * Tudo o que é navegação vive à esquerda em largura constante — a memória
 * muscular de quem usa isto oito horas por dia vale mais do que qualquer
 * layout que se reorganiza sozinho.
 *
 * No telemóvel as quatro colunas não cabem (72 + 248 + conversa + 240 num ecrã
 * de 390px), e a resposta NÃO é encolher: é tirar as duas laterais do fluxo e
 * pô-las por cima, em gavetas. O que fica no ecrã é o que a app é — a conversa.
 * A árvore de componentes é a MESMA nos dois casos; muda a folha de estilo e um
 * par de botões. Um `<MobileShell>` paralelo teria sido dois esqueletos a
 * divergir a cada funcionalidade nova, que é como uma versão móvel apodrece.
 */

import { Limits } from "@greenvoice/shared";
import { useEffect } from "react";
import { useOfficePresence } from "../hooks/useOfficePresence";
import { useIsMobile, useViewportVar } from "../lib/media";
import { gatewayClient, useStore } from "../store/store";
import { ChannelSidebar, useSidebarCollapsed } from "./ChannelSidebar";
import { CharacterPicker } from "./CharacterPicker";
import { ChatView } from "./ChatView";
import { ConnectionBanner } from "./ConnectionBanner";
import { ErrorBoundary } from "./ErrorBoundary";
import { LigacaoPainel } from "./LigacaoPainel";
import { MemberList } from "./MemberList";
import { MessageSearch } from "./MessageSearch";
import { OfficeSpace } from "./OfficeSpace";
import { ServerRail } from "./ServerRail";
import { TopBar } from "./TopBar";
import { UserPanel } from "./UserPanel";
import { VoiceBar } from "./VoiceBar";

export function AppShell() {
  const activeServerId = useStore((s) => s.activeServerId);
  const activeChannelId = useStore((s) => s.activeChannelId);
  const linked = useStore((s) => s.connectionStatus === "ready");
  const viewMode = useStore((s) => s.viewMode);
  const setViewMode = useStore((s) => s.setViewMode);
  const navOpen = useStore((s) => s.navOpen);
  const memberListOpen = useStore((s) => s.memberListOpen);
  const searchOpen = useStore((s) => s.searchOpen);
  const closeSearch = useStore((s) => s.closeSearch);
  const closeDrawers = useStore((s) => s.closeDrawers);

  // Estreito = gavetas. O escritório 2D existe em todos os aparelhos — no
  // telemóvel anda-se com o joystick de toque (ver `OfficeTouchControls`).
  const mobile = useIsMobile();

  // Coluna de canais recolhida à tira estreita. A regra vive na coluna (é ela
  // que a resposta descreve); aqui só se veste o invólucro, que é quem manda na
  // largura — o filho não pode encolher a coluna a partir de dentro.
  const navMini = useSidebarCollapsed();

  // A altura verdadeira do ecrã em `--app-height`, para o teclado virtual não
  // empurrar o compositor para fora do campo de visão.
  useViewportVar();

  // O telemóvel não vê o escritório 2D, mas está lá dentro: Recepção ao entrar
  // no servidor, sala da call ao entrar numa call. Quem está ao computador tem
  // de ver quem está ao telemóvel. Não renderiza nada; o porquê está no hook.
  useOfficePresence();

  // A lista de membros de um servidor é PREGUIÇOSA: o Ready só traz o próprio
  // (ready.service.ts — "a lista completa de cada servidor vem por
  // RequestMembers"). Sem a pedir, toda a gente com quem não temos DM/amizade
  // aparecia como "Alguém": os avatares do escritório, o roster de quem-está-
  // onde, os nomes na call e nas mesas de jogo saem todos de `state.users`/
  // `state.members`, e lá só estava o próprio. Pedimo-la assim que um servidor
  // fica activo e a ligação está de pé — o servidor responde com um
  // `ServerMemberJoin` por membro, que já hidrata as duas tabelas. Corre também
  // a cada reconexão (o Ready reescreve `members` só com o próprio) e é
  // idempotente. Limite honesto: uma página de `membersPerFetch.max` — chega
  // para o escritório de uma empresa; um servidor maior que isso pediria
  // paginação com cursor, que o protocolo ainda não devolve.
  useEffect(() => {
    if (activeServerId === null || !linked) return;
    gatewayClient()?.requestMembers(activeServerId, undefined, Limits.membersPerFetch.max);
  }, [activeServerId, linked]);

  // O escritório é a vista PRIMÁRIA de um servidor: quando activo, ocupa TODO o
  // campo do GreenVoice — a barra de canais, o chat e a lista de membros dão
  // lugar ao mundo 2D. Só fica a rail de servidores à esquerda (a navegação
  // entre servidores/@me). O GreenVoice reaparece de duas formas: o ecrã da call
  // sobreposto ao entrar numa sala, e o botão "Discord" do escritório (vista
  // secundária completa). Em "@me" (DMs) não há escritório: cai sempre no chat.
  const inOffice = viewMode === "office" && activeServerId !== null;

  // Escolher um canal FECHA a gaveta. Sem isto, tocar num canal deixava a gaveta
  // aberta por cima da conversa que se acabou de pedir — o gesto seguinte era
  // sempre fechá-la à mão, e uma app que obriga a dois toques onde devia ser um
  // sente-se logo mal feita.
  useEffect(() => {
    if (!mobile) return;
    closeDrawers();
  }, [mobile, activeChannelId, activeServerId, closeDrawers]);

  const drawerOpen = mobile && (navOpen || memberListOpen);

  // Escape fecha, como qualquer camada por cima. Só está ligado enquanto há uma
  // gaveta aberta: um ouvinte de teclado permanente em `window` é a forma
  // discreta de roubar a tecla a toda a gente.
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeDrawers();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen, closeDrawers]);

  return (
    <div className="shell">
      {/* A ligação privada: por cima de tudo e fora do escritório, para não cair
          quando se muda de vista. */}
      <LigacaoPainel />
      {/* Por cima de tudo e antes de tudo: quem nunca escolheu a personagem
          escolhe-a aqui, uma vez. Vive na shell e não numa vista porque não é
          assunto do chat nem do escritório — é a primeira entrada na app. */}
      <CharacterPicker />
      <ConnectionBanner />
      {/* A barra de cima é IRMÃ da linha, não filha de nenhuma das vistas: é
          por isso que ela sobrevive à troca entre o chat e o escritório com a
          porta sempre no mesmo pixel. Atravessa a largura toda, rail incluída —
          a rail nunca assumiu começar no topo do ecrã. */}
      <TopBar />
      <div className={`shell__row${inOffice ? " shell__row--office" : ""}`}>
        {inOffice ? (
          <>
            <ServerRail />
            {/*
             * A única fronteira de erro dentro da app, e é aqui por uma razão
             * concreta: o escritório são 3600 linhas de canvas, física e Phaser
             * a correr sessenta vezes por segundo — o sítio onde uma excepção de
             * render é mais provável, e de longe. Sem esta fronteira, um erro a
             * desenhar uma árvore desmontava a árvore de componentes TODA: a
             * conversa, a lista de membros e os controlos da chamada iam atrás.
             *
             * A saída é voltar ao chat, e não recarregar: o motor de voz é um
             * singleton de módulo, por isso quem estava numa call continua a
             * ouvir e a ser ouvido enquanto isto está no ecrã — desmontou-se o
             * DESENHO da sala, não a ligação. O `setViewMode("chat")` traz de
             * volta a `VoiceBar` com os controlos, e de caminho tira este
             * cliente do mapa: enquanto o `viewMode` for "office", os outros
             * continuam a ver um avatar fantasma parado no sítio onde rebentou.
             */}
            <ErrorBoundary
              zone="office"
              title="O escritório deixou de responder."
              text="A conversa continua a funcionar, e se estavas numa chamada continuas ligado — só o mundo 2D é que parou."
              retryLabel="Voltar ao chat"
              onRetry={() => setViewMode("chat")}
            >
              <OfficeSpace serverId={activeServerId} />
            </ErrorBoundary>
          </>
        ) : (
          <>
            {/*
             * Rail + canais movem-se como uma só peça, porque é isso que são: a
             * navegação. No desktop este invólucro é `display: contents` — não
             * existe para o layout, e as duas colunas continuam a ser filhas
             * directas da linha, exactamente como sempre foram.
             *
             * `inert` e não `display: none`: a gaveta fechada continua montada
             * (é ela que desliza), e sem `inert` o Tab passeava-se por uma
             * dúzia de botões invisíveis fora do ecrã.
             */}
            <div id="app-nav" className={`shell__nav${navOpen ? " is-open" : ""}`} inert={mobile && !navOpen}>
              <ServerRail />
              <div className={`shell__sidebar${navMini ? " nav-mini" : ""}`}>
                <ChannelSidebar />
                <VoiceBar />
                <UserPanel />
              </div>
            </div>
            <ChatView />
            {/* A procura OCUPA a coluna da direita, não a duplica: é a troca que
                o Discord faz, e é ela que dá largura aos resultados (que são
                mensagens inteiras, de canais diferentes) sem espremer a
                conversa. A lista de membros volta ao fechar a procura. */}
            {searchOpen && activeServerId !== null ? (
              <MessageSearch serverId={activeServerId} onClose={closeSearch} />
            ) : (
              <MemberList />
            )}
            {drawerOpen ? (
              <button type="button" className="shell__scrim" aria-label="Fechar menu" onClick={closeDrawers} />
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
